<template>
  <div class="selectable-trading-list" :style="{ height: `${tableHeight}px` }">
    <!-- Header -->
    <div class="header pt-4 pb-2 px-3">
      <div class="text-16 text-bold">
        {{ $t('BreadCrumbs.Trading List') }}
      </div>
      <el-button
        v-if="!newFlag && canAddNewTrading"
        type="primary"
        icon="el-icon-plus"
        size="mini"
        @click="onCreate"
      >
        Trading
      </el-button>
    </div>

    <!-- Filter -->
    <div class="px-2">
      <TwTableFilterBar
        class="flex-grow py-1"
        :filterSchema="filterSchema"
        :filterModel="filterForm"
        :filterChips="filterChips"
        @apply="onApply"
        @remove="removeFilter"
        @reset="resetFilters"
        @change-filter="handleChangeFilter"
      />
    </div>

    <!-- List -->
    <div class="card-list" ref="scrollContainer">
      <div v-if="loading">
        <div v-for="n in 10" :key="n" class="dummy mb-2">
          <TradingRow />
        </div>
      </div>
      <div v-else>
        <div
          v-for="(item, index) in tradingItems"
          :key="index"
          :class="[
            'card-item',
            {
              selected:
                localSelectedTradingId &&
                localSelectedTradingId === item.tradingId &&
                !newFlag,
            },
          ]"
          @click="() => selectCard(item)"
        >
          <div class="card-item-content">
            <TradingRow :item="item" />
          </div>
        </div>
      </div>
    </div>

    <!--  -->
    <div class="list-footer flex align-center justify-between px-2">
      <el-button
        type="text"
        size="mini"
        @click="prevPage"
        :disabled="currentPage === 1"
        >Prev</el-button
      >
      <!-- <span>{{ currentPage }}/{{ pageCount }}</span> -->
      <div class="my-2 px-2 text-center text-10" style="color: #ccc">
        <!-- {{ currentTotal }} item shown -->
        {{ currentEnd === 0 ? '0' : `${currentStart} ~ ${currentEnd}` }} item shown
      </div>
      <el-button
        type="text"
        size="mini"
        @click="nextPage"
        :disabled="currentPage === pageCount"
        >Next</el-button
      >
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import {
  defineComponent,
  ref,
  toRef,
  watch,
  reactive,
  computed,
  onMounted,
  nextTick,
} from '@vue/composition-api';
import dayjs from 'dayjs';

// lib-tw-common
import {
  TRADING_SEARCH_SORT_TARGET,
  DEFAULT_PAGENATION,
  API_USE_CODE,
  EDITABLE_FLG,
  TRADING_REGISTRATION_STATUS,
  LANGUAGE_SETTING,
  TRADING_PROGRESS_STATUS_VARIABLES,
} from 'lib-tw-common';

import { tradingListFilterSchema } from '@/dictionaries/vue3/tradingList/tradingListFilterSchema';

import { useBffTradingUnitSearch } from '@/composables/useBffTradingUnitSearch';
import { useFilterSchema } from '@/composables/useFilterSchema';

import { notify } from '@/libs/notify';
import { convertFilterFormToQuery } from '@/utils/filterUtil';
import { initialFlag, forcedCloseNotHiddenAndHidden } from '@/utils/searchUtil';

// vue3 components
import TwTableFilterBar from '@/components/vue3/TwTableFilterBar.vue';
// import { ownerSection } from '@/sampleData/store';
import TradingListFilter from './TradingListFilter.vue';
import TradingRow from './tradingRow.vue';

export default defineComponent({
  name: 'SelectableTradingList',
  components: {
    TwTableFilterBar,
    TradingListFilter,
    TradingRow,
  },
  props: {
    selectedTradingId: {
      type: String,
      default: null,
    },
    selectedItem: {
      type: Object,
    },
    newFlag: {
      type: Boolean,
      default: () => false,
    },
    canAddNewTrading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit, root }) {
    const currentPage = ref(1);
    const pageSize = ref(50);
    const tableHeight = ref(500);
    const selectedIndex = ref(0);
    const localSelectedTradingId = ref(props.selectedTradingId);
    const scrollContainer = ref(null);
    const isFilterRefresh = ref(false);

    // offset計算
    const offset = computed(() => (currentPage.value - 1) * pageSize.value);

    const selectableUpdateDateRange =
      root.$store.getters.getSystemMasterData(
        'SYS_MST_TRIAL_PERIOD',
        'TRADING_LIST',
      )?.value || 180;

    // 初期フィルター
    const initialValues = {
      flowUpdateDatetime: [
        dayjs()
          .add(-selectableUpdateDateRange + 1, 'day')
          .format('YYYY-MM-DD 00:00:00'),
        dayjs().format('YYYY-MM-DD 23:59:59'),
      ],
    };

    const {
      form: filterForm,
      chips: filterChips,
      filterSchema,
      updateOptions,
      setFormValues,
    } = useFilterSchema(tradingListFilterSchema, initialValues);

    const ownerSectionLists = computed(
      // vue3ではuseStoreにする必要あり
      () => root.$store.getters.getOwnerSectionLists,
      // () => ownerSection,
    );

    watch(ownerSectionLists, newVal => {
      updateOptions('ownerSectionIds', newVal);
    });

    let firstFetchDone = false;
    const formQueries = convertFilterFormToQuery(filterForm);

    const tradingItems = computed(() => {
      const rawList = data.value?.tradingUnitList || [];
      return rawList.map(item => ({
        tradingId: item.tradingId,
        tradingSubmitStatusCd: item.tradingSubmitStatusCd,
        tradingName: item.tradingName,
        companyName: item.ownerCompanyName,
        companyShortName: item.ownerCompanyShortName,
        updateDate: item.lastFlowUpdateDate,
      }));
    });

    const queries = computed(() => {
      return {
        ...formQueries,
        initialFlag: initialFlag(formQueries),
        limit: pageSize.value,
        offset: offset.value,
        sort: null,
        apiUseCode: API_USE_CODE.TRADING_LIST,
      };
    });

    // BFFから情報取得
    const { data, loading, refresh } = useBffTradingUnitSearch(
      queries.value,
      false,
    );

    const total = computed(() => data.value?.totalCount || 0);
    // const currentTotal = computed(() => tradingItems.value.length * currentPage.value,
    const currentStart = computed(() => offset.value + 1);
    const currentEnd = computed(() => offset.value + tradingItems.value.length,
    );
    const pageCount = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const rules = computed(() => {
      const message = this.isEngLish
        ? `Please input within ${this.selectableUpdateDateRange} days`
        : `${this.selectableUpdateDateRange}日間以内で入力してください`;
      return {
        flowUpdateDatetime: [
          {
            message,
            validator: (rule, value, callback) => {
              // ruleが1つのみのためexistValidationErrorの値もここで管理とする
              if (value?.length !== 2 || this.checkSystemMasterDaysOver()) {
                this.existValidationError = true;
                callback(new Error(rule.message));
              } else {
                this.existValidationError = false;
                callback();
              }
            },
            trigger: 'change',
          },
        ],
      };
    });

    /* ========== Watch ========== */

    watch(
      filterForm,
      newVal => {
        if (!firstFetchDone) {
          const newQuery = convertFilterFormToQuery(newVal);
          const queries = {
            ...newQuery,
            initialFlag: initialFlag(formQueries),
            limit: pageSize.value,
            offset: offset.value,
            sort: null,
            apiUseCode: API_USE_CODE.TRADING_LIST,
          };

          // refresh(queries); // API 呼び出し
        }
        firstFetchDone = true;
      },
      { deep: true, immediate: true },
    );

    // flowUpdateDatetime が空や undefined の場合に初期値をセット
    watch(
      () => filterForm.flowUpdateDatetime,
      val => {
        if (!val || val.length === 0) {
          filterForm.flowUpdateDatetime = initialValues.flowUpdateDatetime;
        }
      },
      { immediate: true }, // 初期化時にもチェックしたい場合
    );

    const scrollToSelectedCard = () => {
      if (!localSelectedTradingId.value) return;
      nextTick(() => {
        if (!scrollContainer.value) return;
        const selectedEl = scrollContainer.value.querySelector(
          '.card-item.selected',
        );
        if (selectedEl) {
          selectedEl.scrollIntoView({ block: 'nearest' });
        }
      });
    };

    watch(
      () => data.value,
      (_newVal, oldVal) => {
        if (localSelectedTradingId.value) {
          const trading = _.find(tradingItems.value, {
            tradingId: localSelectedTradingId.value,
          });
          if (trading) {
            emit('select', trading);
          } else if (oldVal === null || isFilterRefresh.value) {
            // 初回ロード時、またはフィルター変更時に選択中取引が見つからない場合は先頭を選択
            const firstItem = tradingItems.value[0];
            localSelectedTradingId.value = firstItem ? firstItem.tradingId : '';
            emit('select', firstItem);
          }
          // ページ遷移時：見つからなくても emit しない（右ペインをクリアしない）
        } else {
          const firstItem = tradingItems.value[0];
          if (firstItem) {
            localSelectedTradingId.value = firstItem.tradingId;
          }
          emit('select', firstItem);
        }
        isFilterRefresh.value = false;
        scrollToSelectedCard();
      },
      { immediate: true },
    );

    const selectCard = item => {
      // selectedIndex.value = index;
      localSelectedTradingId.value = item.tradingId;
      emit('select', item);
    };

    const updateTableHeight = () => {
      const offset = 100;
      tableHeight.value = window.innerHeight - offset;
    };

    const onCreate = () => {
      emit('handleCreate');
    };

    const onApply = () => {
      isFilterRefresh.value = true;
      currentPage.value = 1;
      updateTrading();
    };

    const removeFilter = key => {
      const target = filterSchema.value.find(item => item.key === key);
      if (!target) return;
      filterForm[key] =
        target.type === 'daterange'
          ? ['', '']
          : target.default !== undefined
          ? target.default
          : '';
      filterForm.flowUpdateDatetime = initialValues.flowUpdateDatetime;
      isFilterRefresh.value = true;
      currentPage.value = 1;
      updateTrading();
    };

    const resetFilters = () => {
      filterSchema.value.forEach(item => {
        filterForm[item.key] =
          item.type === 'daterange'
            ? ['', '']
            : item.default !== undefined
            ? item.default
            : '';
      });
      filterForm.flowUpdateDatetime = initialValues.flowUpdateDatetime;
      isFilterRefresh.value = true;
      currentPage.value = 1;
      updateTrading();
    };

    const updateTrading = () => {
      root.$store.commit('SET_QUERIES', {
        key: 'TRADING_LIST_V3',
        queries: _.cloneDeep(filterForm),
      });
      const query = convertFilterFormToQuery(filterForm);
      query.forcedClose = forcedCloseNotHiddenAndHidden(formQueries); // 左ペインの取引一覧(SelectableTradingList.vue)での設定。src\utils\searchUtil.jsの【取引一覧のforcedClose補足】参照。
      const queries = {
        ...query,
        initialFlag: initialFlag(formQueries),
        limit: pageSize.value,
        offset: offset.value,
        sort: null,
        apiUseCode: API_USE_CODE.TRADING_LIST,
      };

      // flowUpdateDateFrom/flowUpdateDateToが指定されている場合、TradingFlowList側の検索にも同じ条件を連携する
      if (query.flowUpdateDateFrom || query.flowUpdateDateTo) {
        emit('flowUpdateDatetimeChange', [...filterForm.flowUpdateDatetime]);
      }

      refresh(queries);
    };

    const nextPage = () => {
      goToPage(currentPage.value + 1);
      if (scrollContainer.value) {
        scrollContainer.value.scrollTop = 0;
      }
    };
    const prevPage = () => {
      goToPage(currentPage.value - 1);
      if (scrollContainer.value) {
        scrollContainer.value.scrollTop = 0;
      }
    };

    const goToPage = page => {
      if (page >= 1 && page <= pageCount.value) {
        currentPage.value = page;
        updateTrading();
      }
    };

    const handleChangeFilter = data => {
      // console.log('handleChangeFilter: ', data);
    };

    /* ========== Lifecycle ========== */

    onMounted(() => {
      updateOptions('ownerSectionIds', ownerSectionLists.value);
      const queries = root.$store.getters.getQueries('TRADING_LIST_V3');
      if (queries) {
        // 保存済みの選択があっても初期表示時は日付をデフォルトに更新
        queries.flowUpdateDatetime = [
          dayjs()
            .add(-selectableUpdateDateRange + 1, 'day')
            .format('YYYY-MM-DD 00:00:00'),
          dayjs().format('YYYY-MM-DD 23:59:59'),
        ];
        setFormValues(queries);
      }
      // URL に fid があれば取引 ID フィルターを自動適用し、ユーザーに通知する
      const initialFid = props.selectedTradingId;
      if (initialFid && filterForm.tradingId !== initialFid) {
        filterForm.tradingId = initialFid;
        notify.info({
          title: '取引IDでフィルター表示中',
          message: 'すべての取引を表示する場合は、フィルターを解除してください',
          duration: 5000,
        });
      }
      updateTrading();
      nextTick(() => {
        updateTableHeight();
      });
      window.addEventListener('resize', updateTableHeight);
    });

    return {
      loading,
      scrollContainer,
      tradingListFilterSchema,
      filterSchema,
      tableHeight,
      tradingItems,
      total,
      // currentTotal,
      currentStart,
      currentEnd,
      selectedIndex,
      localSelectedTradingId,
      rules,
      pageCount,
      selectCard,
      filterForm,
      filterChips,
      onCreate,
      onApply,
      removeFilter,
      resetFilters,
      onMounted,
      nextTick,
      currentPage,
      nextPage,
      prevPage,
      handleChangeFilter,
    };
  },
});
</script>

<style scoped>
.selectable-trading-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: 1px 1px 4px #ddd;
  padding: 0;
  overflow: hidden;
  position: relative;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
}
.card-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0px;
  min-height: 0;
}
.card-item {
  border-bottom: 1px solid #eee;
  padding: 8px 16px;
  cursor: pointer;
}
.card-item.selected,
.card-item:hover {
  background-color: #f6f5f9;
}

/* スクロールバーのカスタマイズ */
.card-list::-webkit-scrollbar {
  width: 6px;
}
.card-list::-webkit-scrollbar-track {
  background: transparent;
}
.card-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.15);
  border-radius: 3px;
  transition: background-color 0.2s;
}
.card-list:hover::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.15);
}
.list-footer {
  background: #fff;
  border-top: 1px solid #eee;
  padding: 6px 8px;
  z-index: 5;
}

</style>
